import type { Message, MessageSource, WidgetConfig } from '../types/messages';
import type { MessageListElements } from './MessageList';
import type { InputAreaElements } from './InputArea';
import { createMessageList, addMessage, updateMessage, showLoading, hideLoading } from './MessageList';
import { createInputArea, setInputDisabled, focusInput } from './InputArea';

/**
 * Main chat window component.
 * Contains header, message list, and input area.
 */

// Chevron-down: the close button minimises the window, it does not destroy the chat.
const CHEVRON_DOWN_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none" aria-hidden="true">
  <path d="M3.5 6L8 10.5L12.5 6" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

export interface ChatWindowProps {
  config: WidgetConfig;
  messages: Message[];
  isOpen: boolean;
  onClose: () => void;
  onSend: (message: string) => void;
  onCopyCode?: (code: string) => void;
  onFeedback?: (messageId: string, rating: 'up' | 'down') => void;
  greetingTitle?: string;
  greetingSubtitle?: string;
}

export interface ChatWindowElements {
  container: HTMLDivElement;
  messageList: MessageListElements;
  inputArea: InputAreaElements;
}

export function createChatWindow(props: ChatWindowProps): ChatWindowElements {
  const {
    config,
    messages,
    isOpen,
    onClose,
    onSend,
    onCopyCode,
    onFeedback,
    greetingTitle,
    greetingSubtitle,
  } = props;

  // Main container
  const container = document.createElement('div');
  container.className = `chat-window ${config.anchor.replace('_', '-')} ${isOpen ? 'visible' : 'hidden'}${config.message_style === 'flat' ? ' message-style-flat' : ''}`;

  // Header
  const header = createHeader(config, onClose);
  container.appendChild(header);

  // Message list
  const messageList = createMessageList({
    messages,
    avatarUrl: config.avatar_url,
    onCopyCode,
    onFeedback,
    greetingTitle,
    greetingSubtitle,
  });
  container.appendChild(messageList.container);

  // Input area
  const inputArea = createInputArea({
    placeholder: config.placeholder,
    onSend,
  });
  container.appendChild(inputArea.container);

  return { container, messageList, inputArea };
}

/**
 * Create the header element.
 */
function createHeader(config: WidgetConfig, onClose: () => void): HTMLDivElement {
  const header = document.createElement('div');
  header.className = 'chat-header';

  const avatarRing = document.createElement('div');
  avatarRing.className = 'header-avatar-ring';

  const avatar = document.createElement('img');
  avatar.className = 'header-avatar';
  avatar.src = config.avatar_url;
  // Decorative: the title beside it already names the assistant.
  avatar.alt = '';
  avatar.loading = 'eager';

  const presence = document.createElement('span');
  presence.className = 'header-presence';
  presence.setAttribute('aria-hidden', 'true');

  avatarRing.appendChild(avatar);
  avatarRing.appendChild(presence);

  const meta = document.createElement('div');
  meta.className = 'header-meta';

  const title = document.createElement('div');
  title.className = 'header-title';
  title.textContent = config.title;

  const scope = document.createElement('div');
  scope.className = 'header-scope';

  const scopeText = document.createElement('span');
  scopeText.className = 'header-scope-text';
  scopeText.textContent = config.header_scope ?? 'Answers from your docs';

  const scopeSeparator = document.createElement('span');
  scopeSeparator.className = 'header-scope-sep';
  scopeSeparator.setAttribute('aria-hidden', 'true');
  scopeSeparator.textContent = '·';

  const online = document.createElement('span');
  online.className = 'header-online';
  online.textContent = 'online';

  scope.appendChild(scopeText);
  scope.appendChild(scopeSeparator);
  scope.appendChild(online);

  meta.appendChild(title);
  meta.appendChild(scope);

  const closeButton = document.createElement('button');
  closeButton.className = 'close-button';
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Minimize chat');
  closeButton.innerHTML = CHEVRON_DOWN_ICON;
  closeButton.addEventListener('click', onClose);

  header.appendChild(avatarRing);
  header.appendChild(meta);

  if (config.logo_url) {
    const logo = document.createElement('img');
    logo.className = 'header-logo';
    logo.src = config.logo_url;
    logo.alt = '';
    header.appendChild(logo);
  }

  header.appendChild(closeButton);

  return header;
}

/**
 * Show/hide the chat window.
 */
export function setChatWindowVisibility(elements: ChatWindowElements, isOpen: boolean): void {
  elements.container.classList.toggle('visible', isOpen);
  elements.container.classList.toggle('hidden', !isOpen);

  if (isOpen) {
    // Focus input when opening
    setTimeout(() => focusInput(elements.inputArea), 100);
  }
}

/**
 * Add a message to the chat.
 */
export function addChatMessage(
  elements: ChatWindowElements,
  message: Message,
  avatarUrl?: string,
  onCopyCode?: (code: string) => void,
  onFeedback?: (messageId: string, rating: 'up' | 'down') => void
): void {
  addMessage(elements.messageList, message, avatarUrl, onCopyCode, onFeedback);
}

/**
 * Update a streaming message.
 * Returns Promise that resolves when async rendering is complete.
 */
export function updateChatMessage(
  elements: ChatWindowElements,
  messageId: string,
  content: string,
  isStreaming: boolean,
  onCopyCode?: (code: string) => void,
  onFeedback?: (messageId: string, rating: 'up' | 'down') => void,
  sources?: MessageSource[]
): Promise<void> {
  return updateMessage(elements.messageList, messageId, content, isStreaming, onCopyCode, onFeedback, sources);
}

/**
 * Show typing indicator.
 */
export function showTypingIndicator(elements: ChatWindowElements): void {
  showLoading(elements.messageList);
}

/**
 * Hide typing indicator.
 */
export function hideTypingIndicator(elements: ChatWindowElements): void {
  hideLoading(elements.messageList);
}

/**
 * Enable/disable input.
 */
export function setInputEnabled(elements: ChatWindowElements, enabled: boolean): void {
  setInputDisabled(elements.inputArea, !enabled);
}
